import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { AppContext } from '../../../context/app';
import { ZoomControls } from '../zoomControls/ZoomControls';
import styles from './mapView.module.scss';

export interface MapViewHandles {
  resetToDefaultPosition: () => void;
}

interface MapViewProps {
  enableZoomControls: boolean;
  children: React.ReactNode;
  calculatePinCoordinates: (
    translation: { x: number; y: number },
    zoomLevel: number,
  ) => void;
}

interface CustomStyle {
  style: React.CSSProperties;
}

export const minZoomLevel = 1;
export const maxZoomLevel = 6;

const MapView: React.ForwardRefRenderFunction<MapViewHandles, MapViewProps> = (
  { enableZoomControls, children, calculatePinCoordinates },
  ref,
) => {
  const factorZoomIn = 2;
  const factorZoomOut = 0.5;
  const startPosition = { x: 0, y: 0 };
  const containerStyle = {
    overflow: 'hidden',
    borderRadius: '35px',
    width: '100%',
    height: '100%',
  };
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState(startPosition);
  const [translation, setTranslation] = useState(startPosition);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const { setMapIsDragging, mapZoomLevel, setMapZoomLevel } =
    useContext(AppContext);

  const resetToDefaultPosition = () => {
    setMapZoomLevel(minZoomLevel);
    setTranslation(startPosition);
  };

  useImperativeHandle(ref, () => ({
    resetToDefaultPosition,
  }));

  useEffect(() => {
    calculatePinCoordinates(
      { x: translation.x * -1, y: translation.y * -1 },
      mapZoomLevel,
    );
  }, [translation, mapZoomLevel]);

  useEffect(() => {
    setMapIsDragging(isDragging);
  }, [isDragging, setMapIsDragging]);

  const adjustZoom = (newZoomLevel: number) => {
    if (!mapContainerRef.current) {
      return;
    }

    const containerRect = mapContainerRef.current.getBoundingClientRect();
    const zoomCenterX = containerRect.width / 2;
    const zoomCenterY = containerRect.height / 2;
    const scaleChange = newZoomLevel / mapZoomLevel;
    let newTranslationX =
      (translation.x - zoomCenterX) * scaleChange + zoomCenterX;
    let newTranslationY =
      (translation.y - zoomCenterY) * scaleChange + zoomCenterY;

    const maxTranslateX =
      containerRect.width - containerRect.width * newZoomLevel;
    const maxTranslateY =
      containerRect.height - containerRect.height * newZoomLevel;
    newTranslationX = Math.min(0, Math.max(newTranslationX, maxTranslateX));
    newTranslationY = Math.min(0, Math.max(newTranslationY, maxTranslateY));

    setTranslation({ x: newTranslationX, y: newTranslationY });
    setMapZoomLevel(newZoomLevel);
  };

  const onZoomIn = () => {
    const newZoomLevel = Math.min(mapZoomLevel * factorZoomIn, maxZoomLevel);
    adjustZoom(newZoomLevel);
  };

  const onZoomOut = () => {
    const newZoomLevel = Math.max(mapZoomLevel * factorZoomOut, minZoomLevel);
    adjustZoom(newZoomLevel);
  };

  const handleMouseMove = (event: { clientX: any; clientY: any }) => {
    if (!isDragging || !mapContainerRef.current) return;

    // Calculate the edges (dimension) of the container
    const containerRect = mapContainerRef.current.getBoundingClientRect();

    // Ensure the image doesn't go out of the container edges
    const maxTranslateX =
      containerRect.width - containerRect.width * mapZoomLevel;
    const maxTranslateY =
      containerRect.height - containerRect.height * mapZoomLevel;

    let newTranslateX = translation.x + (event.clientX - startPos.x);
    let newTranslateY = translation.y + (event.clientY - startPos.y);

    newTranslateX = Math.min(0, Math.max(newTranslateX, maxTranslateX));
    newTranslateY = Math.min(0, Math.max(newTranslateY, maxTranslateY));

    setTranslation({ x: newTranslateX, y: newTranslateY });

    setStartPos({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const root = document.getElementById('root');

  const disableScroll = () => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.documentElement.style.overscrollBehavior = 'none';
    if (root) root.style.touchAction = 'none';
  };

  const enableScroll = () => {
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
    document.documentElement.style.overscrollBehavior = '';
    if (root) root.style.touchAction = 'auto';
  };

  const handleMouseDown = (event: { clientX: any; clientY: any }) => {
    setIsDragging(true);
    setStartPos({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    const target = event.target as HTMLElement;
    if (mapContainerRef.current && mapContainerRef.current.contains(target)) {
      event.stopPropagation();
      disableScroll();
      handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY });
    }
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDragging) {
      event.stopPropagation();
      const touch = event.touches[0];
      handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    event.stopPropagation();
    enableScroll();
    handleMouseUp();
  };

  const clonedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      const customStyle: CustomStyle = {
        style: {
          ...child.props.style,
          transform: `translate(${translation.x}px, ${translation.y}px) scale(${mapZoomLevel})`,
          transformOrigin: '0 0',
          cursor: isDragging ? 'grabbing' : 'grab',
        },
      };

      return (
        <div
          style={containerStyle}
          onMouseDown={(e) => {
            e.preventDefault();
            handleMouseDown(e);
          }}
          onMouseMove={(e) => {
            e.preventDefault();
            handleMouseMove(e);
          }}
          onMouseUp={(e) => {
            e.preventDefault();
            handleMouseUp();
          }}
          onMouseLeave={(e) => {
            e.preventDefault();
            handleMouseUp();
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          {React.cloneElement(child, customStyle)}
        </div>
      );
    }
    return child;
  });

  return (
    <div className={styles.wrapper} ref={mapContainerRef}>
      {clonedChildren}
      {enableZoomControls && (
        <ZoomControls
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          isZoomOutDisabled={mapZoomLevel <= minZoomLevel}
          isZoomInDisabled={mapZoomLevel >= maxZoomLevel}
        />
      )}
    </div>
  );
};

export default forwardRef(MapView);
